import { useEffect } from 'react';
import { growthBookClient } from 'core/featureFlags/growthBookClient';
import { useCurrentContext } from './useCurrentContext';

type GrowthBookAttributes = {
  tenantId?: string;
  realmId?: string;
};

/**
 * `tenantId` carries the tenant NAME, not Tenant.id: that is what GrowthBook
 * rules across Mosaic already match on (mosaic-web sends the same, and live
 * conditions read `tenantId: "release"`). Sending a numeric id here would
 * silently stop those rules matching.
 */
export const buildGrowthBookAttributes = ({
  tenant,
  realmId,
}: {
  tenant?: string;
  realmId?: string;
}): GrowthBookAttributes => ({
  ...(tenant ? { tenantId: tenant } : {}),
  ...(realmId ? { realmId } : {}),
});

/**
 * Keep GrowthBook's targeting attributes in step with the tenant/realm being
 * viewed.
 *
 * There is one client per environment; these attributes don't select a
 * tenant-specific one. They are what a flag's rule condition is evaluated
 * against, so a flag carrying any condition reads its default until they're set.
 *
 * They describe the tenant and realm a super admin is *looking at*, not their
 * own, so Galaxy flags must not be targeted by team or realm.
 *
 * Every run sends the complete current context. `setAttributes` replaces the
 * whole map, and this hook is its only writer, so a full refresh is what keeps
 * a key that has gone undefined from lingering — sending only the keys that
 * happen to be set would strand the others at stale values.
 */
export const useSyncGrowthBookAttributes = () => {
  const { tenant, realmId } = useCurrentContext();

  useEffect(() => {
    growthBookClient.setAttributes(buildGrowthBookAttributes({ tenant, realmId }));
  }, [tenant, realmId]);
};
